/**
 * The persistent mini-player.
 *
 * It renders whenever the global audio player has a track loaded, follows the
 * member from page to page, and is the only place playback controls live for
 * recorded tracks. Nothing here ever starts audio by itself.
 */
import { useSyncExternalStore, useState } from "react";
import {
  Pause,
  Play,
  RotateCcw,
  RotateCw,
  Volume2,
  VolumeX,
  X,
  Loader2,
  Moon,
  Gauge,
  AlertCircle,
} from "lucide-react";
import { Slider } from "@/components/ui/slider";
import {
  subscribe,
  getState,
  toggle,
  stop,
  seek,
  skip,
  setRate,
  setVolume,
  toggleMute,
  setSleepTimer,
  formatTime,
  type AudioPlayerState,
} from "@/lib/audio-player";

const IDLE: AudioPlayerState = {
  track: null,
  status: "idle",
  currentTime: 0,
  duration: 0,
  rate: 1,
  volume: 1,
  muted: false,
  error: null,
  sleepRemaining: null,
  sleepMinutes: null,
};

const SPEEDS = [0.75, 1, 1.25, 1.5];
const SLEEP_OPTIONS = [10, 20, 30, 45, 60];

export function useAudioPlayerState(): AudioPlayerState {
  return useSyncExternalStore(subscribe, getState, () => IDLE);
}

export function GlobalMiniPlayer() {
  const s = useAudioPlayerState();
  const [expanded, setExpanded] = useState(false);
  const [showSleep, setShowSleep] = useState(false);

  if (!s.track) return null;

  const isBusy = s.status === "loading";
  const isPlaying = s.status === "playing";
  const spoken = s.track.kind !== "ambient";
  const duration = s.duration || s.track.durationHint || 0;

  return (
    <div
      className="fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+4.25rem)] z-[70] px-2 lg:bottom-4 lg:start-auto lg:end-4 lg:w-[26rem] lg:px-0"
      role="region"
      aria-label="Audio player"
    >
      <div className="mx-auto max-w-2xl rounded-2xl border border-gold/30 bg-background/95 p-3 shadow-lg backdrop-blur lg:max-w-none">
        {s.error && (
          <p className="mb-2 flex items-start gap-2 text-xs text-destructive" role="status">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>{s.error}</span>
          </p>
        )}

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggle}
            aria-label={isPlaying ? `Pause ${s.track.title}` : `Play ${s.track.title}`}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gold-light/60 text-[var(--gold-deep)] transition hover:bg-gold/25"
          >
            {isBusy ? (
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
            ) : isPlaying ? (
              <Pause className="h-5 w-5" aria-hidden="true" />
            ) : (
              <Play className="h-5 w-5" aria-hidden="true" />
            )}
          </button>

          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-label={expanded ? "Hide player controls" : "Show player controls"}
            className="min-w-0 flex-1 text-start"
          >
            <p className="truncate font-display text-sm font-medium text-foreground">{s.track.title}</p>
            <p className="truncate text-xs text-muted-foreground">
              {isBusy ? "Loading…" : formatTime(s.currentTime)}
              {duration > 0 && ` / ${formatTime(duration)}`}
              {s.track.subtitle ? ` · ${s.track.subtitle}` : ""}
              {s.sleepRemaining !== null && ` · sleep in ${formatTime(s.sleepRemaining)}`}
            </p>
          </button>

          {spoken && (
            <>
              <button
                type="button"
                onClick={() => skip(-15)}
                aria-label="Rewind 15 seconds"
                className="hidden h-11 w-11 items-center justify-center rounded-full text-muted-foreground transition hover:text-foreground sm:inline-flex"
              >
                <RotateCcw className="h-4 w-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => skip(15)}
                aria-label="Forward 15 seconds"
                className="hidden h-11 w-11 items-center justify-center rounded-full text-muted-foreground transition hover:text-foreground sm:inline-flex"
              >
                <RotateCw className="h-4 w-4" aria-hidden="true" />
              </button>
            </>
          )}

          <button
            type="button"
            onClick={stop}
            aria-label="Stop and close the player"
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-muted-foreground transition hover:text-foreground"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="mt-2">
          <Slider
            value={[Math.min(s.currentTime, duration || s.currentTime)]}
            max={duration || 1}
            step={1}
            onValueChange={([v]) => seek(v ?? 0)}
            aria-label="Seek through the track"
          />
        </div>

        {expanded && (
          <div className="mt-3 space-y-3 border-t border-gold/15 pt-3">
            <div className="flex flex-wrap items-center gap-2">
              {spoken && (
                <>
                  <button
                    type="button"
                    onClick={() => skip(-10)}
                    aria-label="Back 10 seconds"
                    className="inline-flex min-h-11 items-center rounded-full border border-gold/30 px-3 text-xs text-foreground"
                  >
                    −10s
                  </button>
                  <button
                    type="button"
                    onClick={() => skip(10)}
                    aria-label="Forward 10 seconds"
                    className="inline-flex min-h-11 items-center rounded-full border border-gold/30 px-3 text-xs text-foreground"
                  >
                    +10s
                  </button>
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <Gauge className="h-3.5 w-3.5" aria-hidden="true" />
                    Speed
                  </span>
                  {SPEEDS.map((rate) => (
                    <button
                      key={rate}
                      type="button"
                      onClick={() => setRate(rate)}
                      aria-pressed={s.rate === rate}
                      aria-label={`Play at ${rate} times speed`}
                      className={`inline-flex min-h-11 items-center rounded-full border px-3 text-xs transition ${
                        s.rate === rate
                          ? "border-gold bg-gold-light/50 text-[var(--gold-deep)]"
                          : "border-gold/30 text-muted-foreground"
                      }`}
                    >
                      {rate}×
                    </button>
                  ))}
                </>
              )}
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={toggleMute}
                aria-label={s.muted ? "Unmute" : "Mute"}
                className="inline-flex h-11 w-11 items-center justify-center rounded-full text-muted-foreground transition hover:text-foreground"
              >
                {s.muted || s.volume === 0 ? (
                  <VolumeX className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <Volume2 className="h-4 w-4" aria-hidden="true" />
                )}
              </button>
              <Slider
                value={[s.muted ? 0 : Math.round(s.volume * 100)]}
                max={100}
                step={1}
                onValueChange={([v]) => setVolume((v ?? 0) / 100)}
                aria-label="Volume"
                className="max-w-[10rem]"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setShowSleep((v) => !v)}
                aria-expanded={showSleep}
                className="inline-flex min-h-11 items-center gap-1.5 rounded-full border border-gold/30 px-3 text-xs text-foreground"
              >
                <Moon className="h-3.5 w-3.5" aria-hidden="true" />
                {s.sleepMinutes ? `Sleep timer · ${s.sleepMinutes} min` : "Sleep timer"}
              </button>
              {showSleep &&
                SLEEP_OPTIONS.map((min) => (
                  <button
                    key={min}
                    type="button"
                    onClick={() => setSleepTimer(min)}
                    aria-label={`Stop playing after ${min} minutes`}
                    className={`inline-flex min-h-11 items-center rounded-full border px-3 text-xs transition ${
                      s.sleepMinutes === min
                        ? "border-gold bg-gold-light/50 text-[var(--gold-deep)]"
                        : "border-gold/30 text-muted-foreground"
                    }`}
                  >
                    {min}m
                  </button>
                ))}
              {showSleep && s.sleepMinutes !== null && (
                <button
                  type="button"
                  onClick={() => setSleepTimer(null)}
                  className="inline-flex min-h-11 items-center rounded-full border border-gold/30 px-3 text-xs text-muted-foreground"
                >
                  Turn off
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
